import { from, retry, switchMap, throwError, timer } from 'rxjs';

export class HttpError extends Error {
  response: Response;
  constructor(response: Response) {
    super(`HTTP error ${response.status}: ${response.statusText}`);
    this.name = 'HttpError';
    this.response = response;
  }
}

export const checkNoContentSuccess = () => {
  return switchMap((res: Response) => {
    if (!res.ok) {
      return throwError(() => new HttpError(res));
    }
    return from(Promise.resolve(res));
  });
};

/**
 * Retry operator that retries a fetch operation with exponential backoff.
 *
 * `retryWithBackoff(3)` retries up to 3 times with exponential backoff,
 * honoring Retry-After header.
 */
export const retryWithBackoff = <T>(count: number) => {
  return retry<T>({
    count,
    delay: (error: any, retryCount: number) => {
      const exponentialBackoff = 1000 * retryCount ** 2;
      if (error instanceof HttpError) {
        return timer(
          Math.max(
            parseRetryAfterHeader(error.response) ?? 0,
            exponentialBackoff
          )
        );
      }
      return timer(exponentialBackoff);
    },
    resetOnSuccess: true,
  });
};

/**
 * Parses the 'Retry-After' header from an HTTP response.
 *
 * This header can be either:
 * - A number of milliseconds to wait before retrying, or
 * - An string date indicating when to retry.
 *
 * @param response - The HTTP Response object to extract the header from.
 * @returns The delay in milliseconds before retrying, or null if the header is missing or invalid.
 */
const parseRetryAfterHeader = (response: Response): number | null => {
  const retryAfter = response.headers.get('retry-after');
  if (!retryAfter) {
    return null;
  }

  const milliseconds = Number(retryAfter);
  if (!Number.isNaN(milliseconds)) {
    const seconds = milliseconds * 1000;
    return seconds;
  }

  const date = Date.parse(retryAfter);
  if (Number.isNaN(date)) {
    return null;
  }

  return date - Date.now();
};
